<script lang="ts">
  import type { Commentable, CommentType } from './types';
  import { commentService } from '../../services/commentService';
  import { ApiError } from '@services/api';

  type Props = {
    parentId: number;
    commentable: Commentable;
    onCancel: () => void;
    onSubmit: (comment: CommentType) => void;
    onError: (error: Error) => void;
  };

  let { parentId, commentable, onCancel, onSubmit, onError }: Props = $props();

  let replyText = $state('');
  let submitting = $state(false);

  async function submitReply(event: Event) {
    event.preventDefault();

    if (!replyText.trim() || submitting) return;
    submitting = true;

    try {
      const comment = await commentService.submitComment(
        commentable,
        replyText,
        parentId,
      );
      onSubmit(comment);
      replyText = '';
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        onError(e);
      } else {
        onError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      submitting = false;
    }
  }
</script>

<form onsubmit={submitReply} class="reply-form">
  <textarea
    bind:value={replyText}
    placeholder="Escreva sua resposta..."
    required
    disabled={submitting}
    rows="2"
  ></textarea>
  <div class="reply-actions">
    <button type="button" onclick={onCancel} disabled={submitting}>
      Cancelar
    </button>
    <button type="submit" disabled={submitting || !replyText.trim()}>
      {submitting ? 'Enviando...' : 'Enviar Resposta'}
    </button>
  </div>
</form>

<style>
  .reply-form {
    margin-top: 8px;
    margin-bottom: 12px;
  }

  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    min-height: 40px;
    margin-bottom: 6px;
    transition: border-color 0.2s ease;

    :global(.dark) :global(#chapter-section) & {
      background-color: #303032;
      color: #dcdcdc;
      border-color: #444;
    }

    &:focus {
      outline: none;
      border-color: #ccc;

      :global(.dark) :global(#chapter-section) & {
        border-color: #666;
      }
    }
  }

  .reply-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  button {
    padding: 6px 12px;
    background-color: #f0f0f0;
    color: #333;
    border: 1px solid #ccc;
    border-radius: 20px;
    cursor: pointer;
    font-weight: 500;
    font-size: 12px;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;

    :global(.dark) :global(#chapter-section) & {
      background-color: #444;
      color: #ddd;
      border-color: #555;
    }
  }

  button:last-child:not(:disabled) {
    background-color: #0079d3;
    color: white;
    border-color: #0079d3;

    :global(.dark) :global(#chapter-section) & {
      background-color: #f5c109;
      color: #1a1a1b;
      border-color: #f5c109;
    }
  }

  button:hover:not(:disabled) {
    background-color: #e0e0e0;
    border-color: #bbb;

    :global(.dark) :global(#chapter-section) & {
      background-color: #555;
      border-color: #666;
    }
  }

  button:last-child:hover:not(:disabled) {
    background-color: #005fa3;
    border-color: #005fa3;

    :global(.dark) :global(#chapter-section) & {
      background-color: #d1a813;
      border-color: #d1a813;
    }
  }

  button:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    border-color: #ddd;
    cursor: not-allowed;

    :global(.dark) :global(#chapter-section) & {
      background-color: #333;
      color: #666;
      border-color: #444;
    }
  }

  @media (max-width: 600px) {
    textarea {
      padding: 8px;
      font-size: 13px;
      min-height: 50px;
      line-height: 1.4;
    }
    button {
      padding: 7px 12px;
      font-size: 12px;
    }
  }
</style>
